import {
  ColumnFilter,
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  RowCustomAction,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Typography } from '@material-ui/core'
import { AssignmentInd, Delete, FindInPage, Public, Warning } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumEstadoLead, EnumTipoPeaje } from '../../../../api'
import { LeadModel } from '../../../../api/Argentia/endpoints/leads/model/leads'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getUserPermissions, hasPermission } from '../../../../helpers/permissionsHelper'
import { HttpErrorProps } from '../../../../hooks'
import { Pages, Resources } from '../../../../model/RBAC/constants'
import {
  DataTableFilter,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../../redux'
import { fetchTolls } from '../../../contracts/contracts.service'
import { mapTollsResponseToSelectOption } from '../../leads.mapper'

interface LeadTableProps {
  title?: string
  data: LeadModel[]
  isLoading: boolean
  onTableAdd?: () => Promise<boolean>
  onRowEvaluateLead?: (tableRow: LeadModel | LeadModel[]) => void
  onRowEvaluateCustomLead?: (tableRow: LeadModel, event?: any) => void
  onRowPublishQuoteMassive?: (tableRow: LeadModel | LeadModel[]) => void
  onRowViewLeadFile?: (tableRow: LeadModel) => void
  onRowViewObjections?: (tableRow: LeadModel) => void
  onRowDeleteLead?: (tableRow: LeadModel) => void
  dataTableFilter: DataTableFilter | undefined
  dataTableSearch: string | undefined
  dataTableErrorProps: (HttpErrorProps & { title?: string }) | undefined
  disableSelectableRows?: boolean
  commodity?: EnumCommodity | undefined
}

export const LeadTable: React.FC<LeadTableProps> = ({
  title,
  data,
  isLoading,
  onTableAdd,
  onRowEvaluateLead,
  onRowEvaluateCustomLead,
  onRowPublishQuoteMassive,
  onRowViewLeadFile,
  onRowViewObjections,
  onRowDeleteLead,
  dataTableFilter,
  dataTableSearch,
  dataTableErrorProps,
  disableSelectableRows = false,
  commodity
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const history = useHistory()
  const [tollOptions, setTollOptions] = useState<SelectOption[]>([])
  const [isFetchingTolls, setIsFetchingTolls] = useState<boolean>(false)
  const { info, error } = useNotify()
  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  const getRowCustomActions = () => {
    const actions: RowCustomAction[] = []
    if (onRowViewLeadFile) {
      actions.push({
        icon: <AssignmentInd fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.viewLeadFile'),
        onClick: onRowViewLeadFile
      })
    }

    if (onRowEvaluateLead) {
      actions.push({
        icon: <FindInPage fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.evaluateLead'),
        onClick: onRowEvaluateLead,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canEvaluate'
      })
    }

    if (onRowEvaluateCustomLead) {
      actions.push({
        icon: <FindInPage fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.evaluateLead'),
        onClick: onRowEvaluateCustomLead,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canEvaluate'
      })
    }

    if (onRowPublishQuoteMassive) {
      actions.push({
        icon: <Public fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.publishQuote'),
        onClick: onRowPublishQuoteMassive,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canPublish'
      })
    }

    if (onRowViewObjections) {
      actions.push({
        icon: <Warning fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.viewObjections'),
        onClick: onRowViewObjections,
        userPermission: UserPermission.EDIT
      })
    }

    if (onRowDeleteLead) {
      actions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('leads:components.leadTable.table.tooltips.deleteLead'),
        onClick: onRowDeleteLead,
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }

  const getMainToolBarCustomActions = () => {
    const actions: CustomAction[] = []

    if (onRowEvaluateLead) {
      actions.push({
        icon: <FindInPage fontSize="default" />,
        tooltip: t('leads:components.leadTable.table.tooltips.evaluateLead'),
        onClick: onRowEvaluateLead,
        userPermission: UserPermission.EDIT
      })
    }

    if (onRowPublishQuoteMassive) {
      actions.push({
        icon: <Public fontSize="default" />,
        tooltip: t('leads:components.leadTable.table.tooltips.publishQuotesMassive'),
        onClick: onRowPublishQuoteMassive,
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'code',
      label: t('leads:components.leadTable.table.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('leads:components.leadTable.table.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'product.description',
      label: t('leads:components.leadTable.table.columns.product'),
      type: DataType.TEXT
    },
    {
      name: 'tollType',
      label: t('leads:components.leadTable.table.columns.tollType'),
      type: DataType.SELECT,
      selectOptions:
        tollOptions.length > 0
          ? tollOptions
          : enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje')
    },
    {
      name: 'leadState',
      label: t('leads:components.leadTable.table.columns.leadState'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoLead, 'enums:EnumEstadoLead')
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    filterType: FilterType.ADVANCED,
    elevation: 0,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    customInsertEditModal: true,
    pagination: true,
    selectableRows: !disableSelectableRows && hasPermission(Resources.Leads, [UserPermission.EDIT]),
    selectToolbarCustomActions: getMainToolBarCustomActions(),
    analytics: {
      customer: getCustomerDomain(),
      view: t('leads:components.leadTable.table.analytics')
    },
    rowsPerPage: 50,
    onRowInsert: onTableAdd,
    rowCustomActions: getRowCustomActions(),
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    let isMounted = true

    const getTollOptions = async () => {
      if (!commodity) return

      setIsFetchingTolls(true)
      try {
        const response = await fetchTolls(commodity)
        if (isMounted && response.success && response.data) {
          setTollOptions(mapTollsResponseToSelectOption(response.data))
        } else if (isMounted) {
          error(t('leads:fetchErrors.tolls'))
        }
      } finally {
        if (isMounted) {
          setIsFetchingTolls(false)
        }
      }
    }

    getTollOptions()

    return () => {
      isMounted = false
    }
  }, [commodity])
  // #endregion Hooks Ends

  return (
    <>
      {isLoading || isFetchingTolls ? (
        <DataTableSkeleton rows={5} elevation={0} />
      ) : (
        <DataTable
          title={
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          }
          columns={columns}
          options={options}
          data={data.map(lead => ({
            ...lead,
            canEvaluate:
              lead.leadState !== EnumEstadoLead.EvaluacionSolicitada &&
              lead.leadState !== EnumEstadoLead.Procesando,
            canPublish: lead.leadState === EnumEstadoLead.Evaluado
          }))}
          info={DataTableErrorHandler(dataTableErrorProps)}
          userPermissions={getUserPermissions(Pages.Leads)}
          appliedFilter={dataTableFilter}
          appliedSearch={dataTableSearch}
        />
      )}
    </>
  )
}
